"use client";

import Banner from "../shared/Banner";
import BannerPlaceholder from "../shared/BannerPlaceholder";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import { useContext, useEffect, useMemo, useRef } from "react";
import { TPartner } from "@/app/types/homePageTypes";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import dynamic from "next/dynamic";
import { usePopupContext } from "@/app/store/context/popupContext";

const NeighborhoodsParent = dynamic(
	() => import("./Neighborhoods/NeighborhoodsParent"),
	{
		loading: () => <div className="min-h-screen bg-white" aria-hidden />,
	}
);

const PrimeLocation = dynamic(() => import("./primeLocation/PrimeLocation"), {
	ssr: false,
	loading: () => (
		<div className="min-h-screen bg-oBlue-150" aria-hidden />
	),
});

const CircleWithTextComponent = dynamic(
	() => import("../shared/CircleWithText/CircleWithTextComponent"),
	{
		ssr: false,
		loading: () => (
			<div className="min-h-[40rem] lg:min-h-screen bg-white" aria-hidden />
		),
	}
);

const FacilitiesAmenities = dynamic(
	() => import("./facilitiesAmenities/FacilitiesAmenities"),
	{
		loading: () => (
			<div className="min-h-[30rem] lg:min-h-[50rem] bg-white" aria-hidden />
		),
	}
);

const Partners = dynamic(() => import("../shared/partners/Partners"), {
	loading: () => <div className="min-h-[20rem] bg-white" aria-hidden />,
});

const WhatsHappening = dynamic(() => import("./whatsHappening/WhatsHappening"), {
	ssr: false,
	loading: () => <div className="min-h-[24rem] bg-white" aria-hidden />,
});

const HomePagePopups = dynamic(() => import("./HomePagePopups"), {
	ssr: false,
});

const HomePageComponent = ({
	data,
	articles,
	destinations,
	fullArticlesData,
	initialBannerImageSrc,
}: any) => {
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
	const { imgSrc, videoSrc } = useMediaGetter(data?.banners?.[0]?.media);
	const bannerImageSrc = imgSrc ?? initialBannerImageSrc;

	const partnerData = useMemo<TPartner[]>(() => {
		if (!data?.our_partners?.[0]?.children) return [];

		return data.our_partners[0].children.map((partner: any) => ({
			imgSrc:
				process.env.NEXT_PUBLIC_STATIC_MEDIA_API_URL +
				partner?.logo?.media_name,
		}));
	}, [data]);

	const {
		isOpen,
		isOtherPopupOpen,
		images,
		selectedImageIndex,
		closePopup,
		popupInnerData,
		setisOtherPopupOpen,
		setPopupInnerData,
	} = usePopupContext();

	useEffect(() => {
		if (!isOpen && !isOtherPopupOpen) {
			window.lenis?.startScroll?.(true);
		}
	}, [isOpen, isOtherPopupOpen]);

	useEffect(() => {
		if (bannerImageSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [bannerImageSrc, setIsPageFetched]);

	const tl = useRef<GSAPTimeline | null>(null);
	useEffect(() => {
		if (isPageFetched === "fetched" && bannerImageSrc) {
			let cancelled = false;

			const timer = setTimeout(async () => {
				const { default: gsap } = await import("gsap");
				if (cancelled) return;

				const html = document.documentElement;

				tl.current?.kill();
				tl.current = gsap.timeline({
					defaults: { force3D: true },
				});
				// Homepage Banner has no TransparentO (oTitle lives on PrimeLocation).
				tl.current
					.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
					.set(".banner .hero-video", {
						clipPath: "inset(50% 50% 50% 50%)",
						scale: 0.75,
						transformOrigin: "center center",
						force3D: true,
					})
					.to(".banner .hero-image img", {
						delay: html.classList.contains("loaded") ? 0.8 : 2.2,
						duration: 1,
						ease: "power2.inOut",
						y: "0%",
						force3D: true,
					})
					.to(
						".banner .hero-image-wrapper",
						{
							duration: 1,
							ease: "power2.inOut",
							scale: 1,
							force3D: true,
						},
						"-=0.25"
					)
					.to(
						".navbar_section",
						{
							duration: 1,
							ease: "power2.inOut",
							y: "0%",
							force3D: true,
						},
						"-=0.5"
					)
					.to(
						".banner .hero-video",
						{
							duration: 1,
							ease: "expo.inOut",
							scale: 1,
							clipPath: "inset(0% 0% 0% 0%)",
							force3D: true,
						},
						"<"
					);
			}, 50);

			return () => {
				cancelled = true;
				clearTimeout(timer);
				tl.current?.kill();
			};
		}
	}, [isPageFetched, bannerImageSrc]);

	const sortedDestinations = useMemo(
		() =>
			destinations
				? [...destinations].sort(
						(a, b) =>
							new Date(b.created_at).getTime() -
							new Date(a.created_at).getTime()
					)
				: [],
		[destinations]
	);

	return (
		<div className="home-page page_wrapper">
			{bannerImageSrc ? (
				<Banner
					imageSrc={`${bannerImageSrc}`}
					title={data?.banners[0].title.en}
					videoSrc={videoSrc || "/owest-banner.mp4"}
					minHeight={"100vh"}
					priority
					optimizedImage
					videoPosition={"5rem"}
					deferScrollEffects
				/>
			) : (
				<BannerPlaceholder minHeight="100vh" />
			)}
			<NeighborhoodsParent imgSrc={imgSrc} destinations={sortedDestinations} />
			<PrimeLocation
				imgSrc={imgSrc}
				optimizedImage
				data={data?.prime_location[0]}
				CircleImages={data?.owest_philosophy?.[0]?.children}
				oTitle={data?.welcome_owest[0]?.title?.en}
				oDescription={data?.welcome_owest[0]?.description?.en}
			/>
			<CircleWithTextComponent
				imgSrc={imgSrc}
				optimizedImage
				data={data?.owest_philosophy?.[0]?.children}
				text={data?.owest_philosophy?.[0]?.title?.en}
			/>
			<FacilitiesAmenities data={data?.facilities_amenities?.[0]} />
			<Partners
				data={partnerData}
				title={data?.our_partners?.[0]?.title?.en}
				imgSrc={imgSrc}
				optimizedImage
				withBtn
				button_text={data?.our_partners?.[0]?.button_1_text?.en}
				button_url={data?.our_partners?.[0]?.button_1_url}
			/>
			<WhatsHappening
				data={articles}
				sameAmenitiesDetailsPage
				optimizedImage
			/>
			{(isOpen || isOtherPopupOpen) && (
				<HomePagePopups
					isOpen={isOpen}
					isOtherPopupOpen={isOtherPopupOpen}
					images={images}
					selectedImageIndex={selectedImageIndex}
					popupInnerData={popupInnerData}
					fullArticlesData={fullArticlesData}
					closePopup={closePopup}
					setisOtherPopupOpen={setisOtherPopupOpen}
					setPopupInnerData={setPopupInnerData}
				/>
			)}
		</div>
	);
};

export default HomePageComponent;
